import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {FooterLocaleSwitcher as LocaleSwitcher} from './FooterLocaleSwitcher';
import {Link} from '../../Link';
import {SlugLocale, LocaleEnum, UrlLocaleEnum, localeToLocaleAlias} from '../../../types/locale';
import {useLocalizedUrl} from '../../../hooks/useLocalizedUrl';
import {SocialMediaIcons} from '../../icon/SocialMediaIcons';
import * as Logo from '../../icon/Logo';
import {openCookieSettings} from '../../cookie/CookieConsentBanner';
import {CustomFooter} from './CustomFooter';
import {Colors} from '../../../types/colorTypes';
import {ProvenExpertWidget} from '../../provenExpert/ProvenExpertWidget';
import {graphql, useStaticQuery} from 'gatsby';

type Props = {
  slugLocales: SlugLocale[];
};

export const zucchettiLink = 'https://www.zucchetti.com';

export const Footer: React.FC<Props> = props => {
  const intl = useIntl();
  const localizeUrl = useLocalizedUrl();
  const localeAlias = localeToLocaleAlias(intl.locale as LocaleEnum);

  const showProvenExpertRating = useStaticQuery(graphql`
    query ShowProvenExpertRating {
      deAT: datoCmsActiveFooter(locale: "de-AT") {
        activeFooter {
          showProvenExpertRating
        }
      }
      deDE: datoCmsActiveFooter(locale: "de-DE") {
        activeFooter {
          showProvenExpertRating
        }
      }
      deCH: datoCmsActiveFooter(locale: "de-CH") {
        activeFooter {
          showProvenExpertRating
        }
      }
      enIE: datoCmsActiveFooter(locale: "en-IE") {
        activeFooter {
          showProvenExpertRating
        }
      }
      enUS: datoCmsActiveFooter(locale: "en-US") {
        activeFooter {
          showProvenExpertRating
        }
      }
    }
  `)[localeAlias].activeFooter.showProvenExpertRating;

  return (
    <footer data-testid='navigation-footer' className='bg-dark py-12'>
      <div className='container max-w-screen-xl'>
        <Link className='w-auto h-35px mb-3 transition-all duration-300 ease-in-out text-white hover:text-primary' to={localizeUrl('/')}>
          <Logo.Small />
        </Link>

        <CustomFooter />

        <div className='grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-6 items-center pt-10 md:pt-20'>
          <div className='my-5 md:my-0 col-span-3 sm:col-span-1'>
            <LocaleSwitcher slugLocales={props.slugLocales} />
          </div>

          <div className='md:flex my-5 md:my-0'>
            <SocialMediaIcons iconColor={Colors.WHITE} />
          </div>

          <ul className='md:flex my-10 col-span-3 xl:my-0 text-sm leading-loose md:leading-normal'>
            <li>
              <Link
                className='text-white font-body mr-5 hover:underline'
                to={`${intl.formatMessage({
                  id: 'footer.termsOfServiceHref'
                })}`}
              >
                <FormattedMessage id='footer.termsOfServiceLabel' />
              </Link>
            </li>
            <li>
              <Link className='text-white font-body mr-5 hover:underline' to={`${intl.formatMessage({id: 'footer.legalNoticeHref'})}`}>
                <FormattedMessage id='footer.legalNoticeLabel' />
              </Link>
            </li>
            <li>
              <Link
                className='text-white font-body mr-5 hover:underline'
                to={`${intl.formatMessage({
                  id: 'footer.privacyPolicyHref'
                })}`}
              >
                <FormattedMessage id='footer.privacyPolicyLabel' />
              </Link>
            </li>
            <li>
              <span
                data-testid='cookie-settings-link'
                onClick={openCookieSettings}
                aria-label='View cookie settings'
                className='text-white font-body hover:underline cursor-pointer'
              >
                <FormattedMessage id='footer.cookieSettings' />
              </span>
            </li>
          </ul>

          <div className='col-span-2 xl:col-span-1 xl:-ml-32 justify-start'>
            <Link
              className='text-white hover:text-primary inline-block'
              to={intl.locale === LocaleEnum.EN ? zucchettiLink : `${zucchettiLink}/${UrlLocaleEnum.DE}/`}
              dataTestId='zucchetti-link'
              aria-label='ready2order | Zucchetti group'
              rel='noopener'
            >
              <Logo.R2OWithZucchetti />
            </Link>

            {showProvenExpertRating && <ProvenExpertWidget />}
          </div>
        </div>
      </div>
    </footer>
  );
};
